import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import apiClient, { endpoints } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { FiBell, FiUser, FiAward, FiCalendar } from 'react-icons/fi';

const TriggerReminderModal = ({ isOpen, onClose, onReminderSent }) => {
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [errors, setErrors] = useState({});
  const { showSuccess, showError } = useToast();

  const fetchCertificates = async () => {
    try {
      setLoadingCertificates(true);
      const response = await apiClient.get('/api/certificates?limit=100'); // Get certificates (max allowed limit)
      if (response.data.success) {
        // Filter certificates that are active and approaching expiry
        const certificatesData = response.data.data?.certificates || response.data.data || [];
        const activeCertificates = certificatesData.filter(cert => {
          const expiryDate = new Date(cert.expiry_date);
          const today = new Date();
          const diffTime = expiryDate - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          // Include certificates expiring within 90 days or already expired
          return diffDays <= 90;
        });
        setCertificates(activeCertificates || []);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      showError('Failed to load certificates');
    } finally {
      setLoadingCertificates(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCertificates();
    }
  }, [isOpen]);

  const handleCertificateChange = (e) => {
    setSelectedCertificate(e.target.value);
    if (errors.certificate) {
      setErrors(prev => ({ ...prev, certificate: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedCertificate) {
      newErrors.certificate = 'Please select a certificate to send reminder for';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post(`/api/reminders/send/${selectedCertificate}`);
      
      if (response.data.success) {
        showSuccess('Reminder sent successfully!');
        onReminderSent && onReminderSent();
        handleClose();
      } else {
        showError(response.data.message || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError('Failed to send reminder. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCertificate('');
    setErrors({});
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Expired ${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 0) {
      return 'Expires today';
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  const getExpiryStatusColor = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'text-red-600'; // Expired
    } else if (diffDays <= 7) {
      return 'text-red-500'; // Critical
    } else if (diffDays <= 30) {
      return 'text-yellow-600'; // Warning
    } else {
      return 'text-blue-600'; // Info
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Trigger Manual Reminder" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Manual Reminder Trigger
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Select a certificate to send an immediate expiry reminder. This will notify the certificate holder via their preferred communication method.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Certificate Selection */}
        <div>
          <label htmlFor="certificate" className="block text-sm font-medium text-gray-700 mb-1">
            <FiAward className="inline h-4 w-4 mr-1" />
            Select Certificate *
          </label>
          {loadingCertificates ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              <span className="ml-2 text-sm text-gray-500">Loading certificates...</span>
            </div>
          ) : certificates.length === 0 ? (
            <div className="text-center py-8">
              <FiAward className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No certificates found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No certificates are approaching expiry or need reminders at this time.
              </p>
            </div>
          ) : (
            <>
              <select
                id="certificate"
                name="certificate"
                value={selectedCertificate}
                onChange={handleCertificateChange}
                className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.certificate ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select a certificate</option>
                {certificates.map(cert => (
                  <option key={cert.id} value={cert.id}>
                    {cert.user?.first_name} {cert.user?.last_name} - {cert.type} (Expires: {formatDate(cert.expiry_date)})
                  </option>
                ))}
              </select>
              {errors.certificate && (
                <p className="mt-1 text-sm text-red-600">{errors.certificate}</p>
              )}
            </>
          )}
        </div>

        {/* Certificate Details */}
        {selectedCertificate && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            {(() => {
              const cert = certificates.find(c => c.id === parseInt(selectedCertificate));
              if (!cert) return null;
              
              return (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900 flex items-center">
                    <FiAward className="h-4 w-4 mr-2" />
                    Certificate Details
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Holder:</span>
                      <div className="flex items-center mt-1">
                        <FiUser className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-gray-900">
                          {cert.user?.first_name} {cert.user?.last_name}
                        </span>
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        {cert.user?.email}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Certificate Type:</span>
                      <div className="text-gray-900 mt-1">{cert.type}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Expiry Date:</span>
                      <div className="flex items-center mt-1">
                        <FiCalendar className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-gray-900">{formatDate(cert.expiry_date)}</span>
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <div className={`mt-1 font-medium ${getExpiryStatusColor(cert.expiry_date)}`}>
                        {getDaysUntilExpiry(cert.expiry_date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || loadingCertificates || !selectedCertificate}
            className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Sending...
              </>
            ) : (
              <>
                <FiBell className="-ml-1 mr-2 h-4 w-4 inline" />
                Send Reminder
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TriggerReminderModal;